import { identity, intersection, map } from 'lodash';
import type {
  Post as AdapterPost,
  Media,
  Image,
  EmbedMedia,
} from '@wix/ambassador-blog-frontend-adapter-public-v2-post-page/types';
import type { DraftPost } from '@wix/ambassador-blog-v3-draft/types';
import type { Post } from '@wix/ambassador-blog-v3-post/types';
import { removeCorruptedEntities } from '@wix/communities-blog-client-common';
import { getCategoryIds } from '../store/categories/categories-selectors';
import { getTranslations } from '../store/translations/translations-selectors';
import type {
  AppState,
  NormalizedPost,
  PostV2,
  RelatedPostV2,
  ImageMetadata,
  VideoMetadata,
} from '../types';

type Origin = '/v2/posts/publications' | '/v3/posts';

type NormalizePostsParams = {
  state: any;
  posts: AdapterPost[] | Post[] | PostV2[] | undefined;
  blogCategoryIds: string[];
  origin?: Origin;
};

export const normalizePosts = ({
  state,
  posts,
  blogCategoryIds,
  origin,
}: NormalizePostsParams): NormalizedPost[] =>
  posts?.map((post) => normalizePost({ state, post, blogCategoryIds, origin })) ?? [];

type NormalizePostParams = {
  state: AppState;
  post: RelatedPostV2 | PostV2 | Post | DraftPost | AdapterPost;
  blogCategoryIds: string[];
  origin?: Origin;
};

export const normalizePost = ({
  state,
  post,
  blogCategoryIds,
  origin,
}: NormalizePostParams): NormalizedPost => {
  const normalizerFn = getPostNormalizerFn(origin);

  const normalized: NormalizedPost = {
    ...normalizerFn(post as any),
    categoryIds: normalizeCategoryIds(post, blogCategoryIds),
  };
  const draft = normalized.draft;

  normalized.content = removeCorruptedEntities(normalized.content);

  if (isDemoUserPost(post, normalized)) {
    normalized.owner.name = getTranslations(state)['demo-user.posts-list.owner-name'] || 'Admin';
    // TODO remove after specs.wixBlog.HeaderImage
    // @ts-expect-error
    normalized.heroImage = undefined;
  }

  if (draft) {
    draft.categoryIds = normalizeCategoryIds(draft, blogCategoryIds);
    draft.content = removeCorruptedEntities(draft.content);
    draft.firstPublishedDate ??= 'firstPublishedDate' in post ? post.firstPublishedDate : undefined;
  }

  return normalized;
};

export const normalizePostEntities = (entities: any[], state: AppState) =>
  map(entities, (entity) =>
    entity.type === 'post'
      ? normalizePost({
          state,
          post: entity,
          blogCategoryIds: getCategoryIds(state),
        })
      : entity,
  );

export const normalizePostV3 = (post: Post | AdapterPost): NormalizedPost => {
  const {
    metrics,
    pinned,
    featured,
    mostRecentContributorId,
    pricingPlanIds,
    minutesToRead,
    commentingEnabled,
    slug,
  } = post;

  const content = post.richContent || post.content;

  return {
    ...post,
    content: typeof content === 'string' ? JSON.parse(content) : content,
    coverImage: normalizePostCoverImage(post),
    firstPublishedDate: post.firstPublishedDate as Date,
    likeCount: metrics?.likes || 0,
    viewCount: metrics?.views || 0,
    totalComments: metrics?.comments || 0,
    averageRating: metrics?.averageRating || 0,
    totalRatings: metrics?.totalRatings || 0,
    isFeatured: featured ?? false,
    isPinned: pinned ?? false,
    isCommentsDisabled: !commentingEnabled,
    lastWriterSiteMemberId: mostRecentContributorId,
    paidPlansGroupIds: pricingPlanIds ?? [],
    timeToRead: minutesToRead ?? 0,
    slugs: [slug as string],
    status: 'published',
    canSeePaidContent: typeof post.preview === 'boolean' ? !post.preview : true,
    ...makeSeoProps(post),
  };
};

export const getOwnerTitle = (post: PostV2) => post?.owner?.name?.trim();

export const discardDeletedCategoryIds = (postCategoryIds: string[], blogCategoryIds: string[]) =>
  intersection(postCategoryIds || [], blogCategoryIds);

const getPostNormalizerFn = (origin: Origin | undefined) => {
  switch (origin) {
    case '/v2/posts/publications':
      return normalizePostPublications;
    case '/v3/posts':
      return normalizePostV3;
    default:
      return identity;
  }
};

const normalizeCategoryIds = (
  post: RelatedPostV2 | PostV2 | Post | AdapterPost | DraftPost | undefined,
  blogCategoryIds: string[],
): string[] =>
  post && 'categories' in post && post.categories?.length
    ? post.categories.map((c) => c.id)
    : discardDeletedCategoryIds(post?.categoryIds || [], blogCategoryIds);

const normalizePostPublications = (post: RelatedPostV2): NormalizedPost => {
  return {
    ...post,
    status: 'published',
    content: undefined,
    isCommentsDisabled: !post.isCommentingEnabled,
    likeCount: post.likesCount,
    viewCount: post.viewsCount,
    totalComments: post.commentsCount,
    averageRating: 0,
    totalRatings: 0,
  };
};

const normalizePostCoverImage = (post: AdapterPost | Post): NormalizedPost['coverImage'] => {
  if (post.media?.wixMedia || post.media?.embedMedia) {
    return handlePostMedia(post.media);
  }

  return undefined;
};

const getImageMetadata = (image: Image | undefined): { imageMetadata: ImageMetadata } | undefined =>
  image && {
    imageMetadata: {
      file_name: image.id || image.filename,
      height: image.height ?? 0,
      width: image.width ?? 0,
    },
  };

const getVideoMetadata = (
  video: EmbedMedia | undefined,
): { videoMetadata: VideoMetadata } | undefined => {
  if (!video) {
    return undefined;
  }

  return {
    videoMetadata: {
      video_url: video.video?.url ?? '',
      thumbnail_height: video?.thumbnail?.height ?? 0,
      thumbnail_width: video?.thumbnail?.width ?? 0,
      thumbnail_url: video?.thumbnail?.url ?? '',
    },
  };
};

const handlePostMedia = (media: Media): NormalizedPost['coverImage'] => {
  const { custom, displayed, wixMedia, embedMedia, altText } = media;
  const src =
    wixMedia?.image?.url ||
    wixMedia?.videoV2?.url ||
    embedMedia?.thumbnail?.url ||
    wixMedia?.videoV2?.posters?.[0]?.url ||
    '';
  const commonParams = {
    isCustom: custom ?? false,
    isEnabled: displayed ?? false,
    isResolved: !custom,
    shouldRender: displayed ?? false,
    src,
    altText,
  };

  if (wixMedia?.videoV2) {
    return {
      ...commonParams,
      videoMetadata: {
        video_url: (wixMedia?.videoV2?.url || wixMedia.videoV2.resolutions?.[0]?.url) ?? '',
        thumbnail_height: wixMedia?.videoV2?.height || wixMedia.videoV2.resolutions?.[0]?.height,
        thumbnail_width: wixMedia?.videoV2?.width || wixMedia.videoV2?.resolutions?.[0]?.width,
        thumbnail_url: wixMedia?.videoV2?.posters?.[0]?.url ?? '',
      },
    };
  }

  return {
    ...commonParams,
    ...getImageMetadata(wixMedia?.image),
    ...getVideoMetadata(embedMedia),
  };
};

const makeSeoProps = (post: Post | AdapterPost) => {
  let canonicalUrl: string | undefined;
  let seoTitle: string | undefined;
  let seoDescription: string | undefined;

  let seoShowInSearch = true;
  let seoShowSnippetInSearch = true;

  post.seoData?.tags?.forEach(({ type, props, children }) => {
    switch (type) {
      case 'link':
        if (props?.rel === 'canonical') {
          canonicalUrl = props.href;
        }
        break;
      case 'title':
        seoTitle = children;
        break;
      case 'meta':
        if (props?.name === 'description') {
          seoDescription = props.content;
        } else if (props?.name === 'robots') {
          seoShowInSearch = !props.content?.includes('noindex');
          seoShowSnippetInSearch = !props.content?.includes('nosnippet');
        }
        break;
      default:
        break;
    }
  });

  return {
    canonicalUrl,
    seoTitle,
    seoDescription,
    seoShowInSearch,
    seoShowSnippetInSearch,
    seoSlug: post.slug,
  };
};

export const isDemoPostHeroImage = (
  image: Post['heroImage'] | AdapterPost['heroImage'],
): boolean => {
  if (!image) {
    return false;
  }

  const HERO_IMAGE_IDs = [
    '6d8832_639454ad77914665966c5a0adbdc1bc1~mv2.jpg',
    '6d8832_b4044962b09243ff9cd65c370e5a4357~mv2.jpg',
    '6d8832_84cf8dd687d54912a20640d298afb3d3~mv2.jpg',
  ];

  return HERO_IMAGE_IDs.includes(image.id as string);
};

const isDemoUserPost = (
  post: RelatedPostV2 | PostV2 | Post | AdapterPost | DraftPost,
  normalized: NormalizedPost,
): normalized is Required<NormalizedPost> => {
  const DEMO_USER_SITE_MEMBER_IDS = [
    '12345678-1234-1234-1234-123456789123',
    '68c56f39-fe67-4f3d-8ae7-991684463a63', // ru
    'eb619033-dfe7-43df-8bd5-c264da83aeb7', // ja
    '24536dc0-3451-4fa0-aaa1-e826b7828bfb', // es
    '537c64ba-b52b-4643-92fc-cc4e011b7a93', // de
    'f2923da0-3a70-4d26-8d80-b3227e9fe973', // fr
    '8a3e6d16-735e-4618-8be7-964643d23fc7', // pt
    'faf5a4cb-1fd4-4fbc-9e31-77a23ee976eb', // ko
  ];

  return (
    'owner' in post &&
    normalized.owner != null &&
    DEMO_USER_SITE_MEMBER_IDS.includes(post.owner?.siteMemberId as string)
  );
};

type GenericMetrics = {
  averageRating?: number;
  comments?: number;
  likes?: number;
  totalRatings?: number;
  views?: number;
};

type GenericPost = {
  id?: string;
  isLiked?: boolean;
};

export function enhancePostsWithMetadata<
  PostType extends GenericPost,
  MetricsType extends GenericMetrics,
>({
  posts,
  postMetricsMap,
  postLikesMap,
}: {
  posts: PostType[];
  postMetricsMap: Record<string, MetricsType>;
  postLikesMap: Record<string, boolean>;
}): PostType[] {
  const defaultMetrics = {
    averageRating: 0,
    comments: 0,
    likes: 0,
    totalRatings: 0,
    views: 0,
  };

  return posts.map((post) => ({
    ...post,
    metrics: (post.id && postMetricsMap[post.id]) || defaultMetrics,
    isLiked: (post.id && postLikesMap[post.id]) || post.isLiked,
  }));
}
